import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { SplitText } from 'gsap/SplitText';
import { wheelAnim } from '../animation/wheelText';
import '../animation/counter';

gsap.registerPlugin(ScrollTrigger, SplitText);

export default class MultistepAnim {
	constructor(next, type) {
		this.type = type;
		this.section = next.container;
		this.bgkColor = this.section.dataset.color;
		this.bodyClasses = next.html.match(/<body.+?class="([^""]*)"/i);

		this.header = document.querySelector('.main-navbar');
		this.footer = document.querySelector('.main-footer');
		this.backBtn = this.section.querySelector('.fp-btn-ter-w.active');
		this.dates = [...this.section.querySelectorAll('.dates .year')];
		this.label = this.section.querySelector('.label');
		this.separator = this.section.querySelector('.dates .separator');
		this.ranking = this.section.querySelector('.ranking');
		this.picture = this.ranking?.querySelector('picture');
		this.pilotCols = [...this.ranking?.querySelectorAll('.pilot-col')];
		this.numbers = [...this.ranking?.querySelectorAll('.pilot-col .h6-500gp')];
		this.titles = [...this.ranking?.querySelectorAll('.pilot-col .body-l-500gp')];
		this.pageSections = [...this.section.querySelectorAll('section')];
		this.heightsMobile = ['55%', '45%', '35%', '25%', '15%'];
		this.heightsTablet = ['34%', '28%', '22%', '16%', '10%'];
		this.triggers = [];

		this.timing = this.type === 'self' ? 0 : 1;
	}

	init() {
		window.scrollTo(0, 0);	

		document.fonts.ready.then(() => {

			const panelTl = gsap.timeline({
				defaults: { force3D: true }
			});

			const pictureST = ScrollTrigger.create({
				trigger: this.picture,
				start: 'top top',
				endTrigger: this.ranking,
				end: 'bottom top',
				pin: true,
				pinSpacing: false
			});

			this.triggers.push(pictureST);

			const labelSplit = this.label
				? SplitText.create(this.label, { type: 'lines', autoSplit: true, mask: 'lines', aria: 'none' })
				: null;

				if (!this.header.classList.contains('is-fixed') && !this.header.classList.contains('top')) {

					panelTl
						.set(this.header, {clearProps: 'all'}, 0)
						.add(() => {
							this.header.classList.add('top');
						}, 0)
				}

				if (this.type !== 'self') {
					panelTl
					.to(document.body, { backgroundColor: this.bgkColor, duration: 1 }, 0)
					.add(() => {
						document.body.setAttribute('class', (this.bodyClasses && this.bodyClasses.at(1)) ?? '');
						gsap.set(document.body, {clearProps: 'all'});
					}, '>')
				}


			panelTl
				.set(this.label, {autoAlpha: 1}, this.timing)
				.to(this.header, {y: 0, duration: 1.0, ease: 'expo.out'}, this.timing)
				.to(this.dates, {autoAlpha: 1, duration: 1, ease: 'power1.inOut'}, this.timing)
				.to(this.separator, {autoAlpha: 1, duration: 1, ease: 'power1.inOut'}, this.timing)
				.to(this.backBtn, { opacity: 1, y: 0, duration: 0.6, ease: 'power2.out' }, this.timing + 0.3);


			this.dates.forEach(date => {
				const wheelTl = wheelAnim(date);
				panelTl.add(wheelTl, 0.2);
			});

			panelTl
				.from(labelSplit?.lines ?? [], {
					yPercent: 100,
					autoAlpha: 0,
					duration: 0.6,
					ease: 'power2.out',
					stagger: 0.08,
				}, 1)
				.add(() => labelSplit?.revert(), '>');

			const mm = gsap.matchMedia();
			mm.add('(max-width: 599px)', () => {
				const afterH = `${window.innerWidth * 16 / 9}px`;

				panelTl
					.to(this.ranking, {opacity: 1, duration: 1, clearProps: "transform"}, 0.2)
					.to(this.pilotCols, {
						height: (i) => this.heightsMobile[i] ?? '15%',
						duration: 2,
						ease: 'expo.out',
						stagger: 0.1,
					}, 0.1)
					.to(this.pilotCols, {
						'--pilot-after-h': afterH,
						duration: 2,
						ease: 'power4.inOut',
						stagger: 0.1,
					}, 0)
					.counter(this.numbers, {
						duration: 2,
						increment: 1,
						stagger: 0.1,
						ease: "linear"
					}, 0.2)
					.to(this.titles, {
						opacity: 1,
						duration: 1,
						ease: 'power1.inOut',
						stagger: 0.1,
					}, 0.2)
			});

			mm.add('(min-width: 600px) and (max-width: 1023px)', () => {
				const afterH = `${window.innerWidth * 4 / 3}px`;

				panelTl
					.to(this.ranking, {autoAlpha: 1, duration: 2, clearProps: "transform"}, 0.6)
					.to(this.pilotCols, {
						height: (i) => this.heightsTablet[i] ?? '10%',
						duration: 1.5,
						ease: 'power4.inOut',
						stagger: 0.2,
					}, 0)
					.to(this.pilotCols, {
						'--pilot-after-h': afterH,
						duration: 2,
						ease: 'power4.inOut',
						stagger: 0.2,
					}, 0)
					.counter(this.numbers, {
						duration: 2,
						increment: 1,
						stagger: 0.2,
						ease: "linear"
					}, 0.4)
					.to(this.titles, {
						opacity: 1,
						duration: 2,
						ease: 'power1.inOut',
						stagger: 0.2,
					}, 0.4)
			});
			mm.add('(min-width: 1024px)', () => {
				const afterH = `${window.innerWidth * 11 / 16}px`;
				
				panelTl
					.to(this.ranking, {opacity: 1, duration: 1, clearProps: "transform"}, this.timing + 0.2)
					.to(this.pilotCols, {
						height: (i) => this.heightsTablet[i] ?? '10%',
						duration: 2,
						ease: 'power4.inOut',
						stagger: 0.4,
					}, this.timing)
					.to(this.pilotCols, {
						'--pilot-after-h': afterH,
						duration: 2,
						ease: 'power4.inOut',
						stagger: 0.4,
					}, this.timing)
					.counter(this.numbers, {
						duration: 2,
						increment: 1,
						stagger: 0.4,
						ease: "linear"
					}, this.timing + 0.4)
					.to(this.titles, {
						opacity: 1,
						duration: 2,
						ease: 'power1.inOut',
						stagger: 0.4,
					}, this.timing + 0.4)
			});

			return panelTl;

		});
	}

	leave(toNamespace) {

		// Blocca il container alla posizione scroll corrente per evitare il salto visivo
		const scrollY = window.scrollY;
		gsap.set(this.section, {
			position: 'fixed',
			top: -scrollY,
			left: 0,
			width: '100%',
			overflow: 'hidden',
		});
		document.body.style.overflow = 'hidden';

			const exitTl = gsap.timeline({
				defaults: { force3D: true }
			});

			if (toNamespace === 'home') {
				window.headerInstance.backToHome();
			}

			exitTl
				.to(this.backBtn, { opacity: 0, y: -8, duration: 0.3, ease: 'power2.in' }, 0)
				.to([this.pageSections, this.footer], { autoAlpha: 0, duration: 1, stagger: 0.05,
				ease: 'power2.inOut' }, 0.3)
				.to([this.ranking, this.dates, this.separator, this.label], { autoAlpha: 0, duration: 1, ease: 'power1.inOut' }, 0.3)
				.to(this.pilotCols, {
					height: '100%',
					duration: 0.8,
					ease: 'power2.inOut',
					stagger: 0.2,
				}, 0.3)
				.to(this.titles, {
					opacity: 0,
					duration: 1,
					ease: 'power2.inOut',
					stagger: 0.1,
				}, 0.3);

			return exitTl;

	}
}
